import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Icon,
  PageHeader,
  Skeleton,
  Tabs,
  TextField,
  Toggle,
  useConfirm,
  useToast,
} from "stories/Admin";
import {
  connectedLabel,
  isStripeKey,
  PaymentSettings,
  paymentSettingsUrl,
} from "./paymentSettings";
import styles from "./PaymentSettingsPage.module.scss";

/*
 * Betaling: moskeen kobler til sin egen Stripe- og Vipps-konto. Pengene går rett til moskeen;
 * iBønn lagrer nøklene kryptert og viser dem aldri igjen (bare de siste 4 tegnene).
 */

type Provider = "vipps" | "stripe";

const STRIPE_KEYS_URL = "https://dashboard.stripe.com/apikeys";
const VIPPS_PORTAL_URL = "https://portal.vippsmobilepay.com";

const STRIPE_PERMISSIONS: Array<[string, string]> = [
  ["Checkout Sessions", "Write"],
  ["Products", "Write"],
  ["Prices", "Write"],
  ["Customers", "Write"],
  ["Subscriptions", "Read"],
  ["Webhook Endpoints", "Write"],
  ["Account / Balance", "Read"],
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

/** Det som må være på plass hos Vipps/Stripe før det virker (moskeen trenger ikke sette opp lenker eller webhooks). */
const Requirements: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className={styles.requirements}>
    <b>Dette må være på plass først</b>
    <ul>{children}</ul>
  </div>
);

const PaymentSettingsPage: React.FC = () => {
  const { selectedOrgNumber } = useAuth();
  const { execute, lastError } = useFetch<any>();
  const api = useRef({ execute, lastError });
  api.current = { execute, lastError };
  const toast = useToast();
  const confirm = useConfirm();

  const [tab, setTab] = useState<Provider>("vipps");
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [stripeKey, setStripeKey] = useState("");
  const [stripeTouched, setStripeTouched] = useState(false);
  const [stripeEditing, setStripeEditing] = useState(false);
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [checked, setChecked] = useState<string[]>([]);

  const [vipps, setVipps] = useState({
    clientId: "",
    clientSecret: "",
    subscriptionKey: "",
    msn: "",
    testMode: false,
  });
  const [vippsEditing, setVippsEditing] = useState(false);
  const [vippsSaving, setVippsSaving] = useState(false);
  const [vippsError, setVippsError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedOrgNumber) return;
    setLoadError(false);
    const result = await api.current.execute(
      paymentSettingsUrl(selectedOrgNumber),
      "GET",
    );
    if (result) setSettings(result);
    else setLoadError(true);
    setIsLoading(false);
  }, [selectedOrgNumber]);

  useEffect(() => {
    setIsLoading(true);
    load();
  }, [load]);

  const errorText = (fallback: string) => {
    const error = api.current.lastError();
    if (error?.code === 503)
      return error.message || "Betaling er ikke satt opp på serveren.";
    return error?.message || fallback;
  };

  const serverReady = settings?.serverReady !== false;
  const stripe = settings?.stripe;
  const vippsStatus = settings?.vipps;

  const saveStripe = async () => {
    setStripeTouched(true);
    if (!isStripeKey(stripeKey) || !selectedOrgNumber) return;
    setStripeSaving(true);
    setStripeError(null);
    const result = await api.current.execute(
      `${paymentSettingsUrl(selectedOrgNumber)}/stripe`,
      "PUT",
      { secretKey: stripeKey.trim() },
    );
    setStripeSaving(false);
    if (result === null) {
      setStripeError(
        errorText("Kunne ikke koble til Stripe. Sjekk nøkkelen og prøv igjen."),
      );
      return;
    }
    setStripeKey("");
    setStripeTouched(false);
    setStripeEditing(false);
    toast.success("Stripe er koblet til.");
    load();
  };

  const saveVipps = async () => {
    if (!selectedOrgNumber) return;
    setVippsSaving(true);
    setVippsError(null);
    const result = await api.current.execute(
      `${paymentSettingsUrl(selectedOrgNumber)}/vipps`,
      "PUT",
      {
        clientId: vipps.clientId.trim(),
        clientSecret: vipps.clientSecret.trim(),
        subscriptionKey: vipps.subscriptionKey.trim(),
        msn: vipps.msn.trim(),
        testMode: vipps.testMode,
      },
    );
    setVippsSaving(false);
    if (result === null) {
      setVippsError(
        errorText(
          "Kunne ikke koble til Vipps. Sjekk at alle fire verdiene er riktige.",
        ),
      );
      return;
    }
    setVipps({
      clientId: "",
      clientSecret: "",
      subscriptionKey: "",
      msn: "",
      testMode: false,
    });
    setVippsEditing(false);
    toast.success("Vipps er koblet til.");
    load();
  };

  const disconnect = async (provider: Provider) => {
    const name = provider === "vipps" ? "Vipps" : "Stripe";
    const ok = await confirm({
      title: `Koble fra ${name}?`,
      message: `Nøklene slettes fra iBønn, og ingen kan gi med ${name} i appen før dere kobler til igjen. Faste gaver som allerede er satt opp, kan slutte å bli trukket. Pengene som allerede er betalt, påvirkes ikke.`,
      confirmLabel: "Koble fra",
      tone: "danger",
    });
    if (!ok || !selectedOrgNumber) return;
    const result = await api.current.execute(
      `${paymentSettingsUrl(selectedOrgNumber)}/${provider}`,
      "DELETE",
    );
    if (result === null) {
      toast.error(errorText(`Kunne ikke koble fra ${name}. Prøv igjen.`));
      return;
    }
    toast.success(`${name} er koblet fra.`);
    load();
  };

  const vippsValid =
    vipps.clientId.trim() &&
    vipps.clientSecret.trim() &&
    vipps.subscriptionKey.trim() &&
    /^\d{4,10}$/.test(vipps.msn.trim());

  const stripeKeyError =
    stripeTouched && !isStripeKey(stripeKey)
      ? "Nøkkelen skal begynne med rk_live_ (eller sk_live_, rk_test_, sk_test_)."
      : undefined;

  const statusBadge = (
    connected: boolean | undefined,
    last4?: string | null,
  ) =>
    connected ? (
      <Badge tone="mint">{connectedLabel(last4)}</Badge>
    ) : (
      <Badge tone="grey">Ikke tilkoblet</Badge>
    );

  const renderStripe = () => {
    const showForm = !stripe?.connected || stripeEditing;
    return (
      <Card
        title="Stripe (kort, Apple Pay og Google Pay)"
        actions={statusBadge(stripe?.connected, stripe?.keyLast4)}
      >
        <Requirements>
          <li>
            Stripe-kontoen må være <b>aktivert</b>: organisasjonsinfo og
            bankkonto for utbetaling må være fylt ut. Ellers virker bare
            testnøkler.
          </li>
          <li>
            Kontoen må ha et <b>offentlig navn</b> under Settings → Public
            details. Stripe Checkout krever det, og det er navnet giveren ser.
          </li>
          <li>
            Lag en <b>begrenset nøkkel</b> med rettighetene i sjekklisten under,
            og lim den inn. Dere trenger ikke sette opp redirect-lenke eller
            webhook; det gjør iBønn.
          </li>
          <li>
            Valgfritt: slå på e-postkvittering under Settings → Emails, så
            giveren får kvittering fra Stripe.
          </li>
        </Requirements>
        {stripe?.connected && (
          <div className={styles.connected}>
            <dl className={styles.dl}>
              {stripe.accountName && (
                <>
                  <dt>Konto</dt>
                  <dd>{stripe.accountName}</dd>
                </>
              )}
              <dt>Nøkkel</dt>
              <dd>…{stripe.keyLast4}</dd>
              <dt>Modus</dt>
              <dd>
                {stripe.testMode ? (
                  <Badge tone="gold">Testmodus</Badge>
                ) : (
                  "Ekte betalinger"
                )}
              </dd>
            </dl>
            <div className={styles.actions}>
              {!stripeEditing && (
                <Button
                  variant="secondary"
                  onClick={() => setStripeEditing(true)}
                  disabled={!serverReady}
                >
                  Bytt nøkkel
                </Button>
              )}
              <Button variant="danger" onClick={() => disconnect("stripe")}>
                Koble fra
              </Button>
            </div>
          </div>
        )}

        {showForm && (
          <div className={styles.form}>
            <ol className={styles.steps}>
              <li>
                Logg inn i moskeens Stripe-konto og åpne{" "}
                <ExternalLink href={STRIPE_KEYS_URL}>
                  Utviklere → API-nøkler
                </ExternalLink>
                .
              </li>
              <li>
                Under «Begrensede nøkler»: trykk «Opprett begrenset nøkkel» og
                kall den «iBønn».
              </li>
              <li>
                Gi nøkkelen rettighetene under. Alt annet kan stå på «Ingen».
              </li>
              <li>
                Kopier nøkkelen (begynner med <code>rk_live_</code>) og lim den
                inn her.
              </li>
              <li>
                Trykk «Koble til Stripe». iBønn sjekker nøkkelen og setter opp
                varsling av betalinger (webhook) i Stripe selv.
              </li>
            </ol>

            <fieldset className={styles.checklist}>
              <legend>Rettigheter nøkkelen må ha</legend>
              {STRIPE_PERMISSIONS.map(([name, level]) => (
                <Checkbox
                  key={name}
                  checked={checked.includes(name)}
                  onChange={(e) =>
                    setChecked(
                      e.target.checked
                        ? [...checked, name]
                        : checked.filter((c) => c !== name),
                    )
                  }
                  label={
                    <span className={styles.permission}>
                      {name}
                      <Badge tone={level === "Write" ? "blue" : "grey"}>
                        {level}
                      </Badge>
                    </span>
                  }
                />
              ))}
            </fieldset>

            <TextField
              label="Begrenset nøkkel"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="rk_live_…"
              value={stripeKey}
              onChange={(e) => setStripeKey(e.target.value)}
              onBlur={() => setStripeTouched(!!stripeKey)}
              error={stripeKeyError}
              help="Nøkkelen lagres kryptert og vises aldri igjen."
              disabled={!serverReady}
            />
            {stripeError && <Alert tone="error">{stripeError}</Alert>}
            <div className={styles.actions}>
              {stripeEditing && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStripeEditing(false);
                    setStripeKey("");
                    setStripeTouched(false);
                    setStripeError(null);
                  }}
                >
                  Avbryt
                </Button>
              )}
              <Button
                onClick={saveStripe}
                loading={stripeSaving}
                disabled={!serverReady || !stripeKey.trim()}
                icon={<Icon name="link" size={16} strokeWidth={2} />}
              >
                Koble til Stripe
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderVipps = () => {
    const showForm = !vippsStatus?.connected || vippsEditing;
    const set = (field: keyof typeof vipps, value: string | boolean) =>
      setVipps({ ...vipps, [field]: value });
    return (
      <Card
        title="Vipps MobilePay"
        actions={statusBadge(vippsStatus?.connected, vippsStatus?.keyLast4)}
      >
        <Requirements>
          <li>
            Moskeen må ha avtale om <b>Faste betalinger</b> (Recurring) for
            månedlige gaver, og <b>Vipps på nett</b> (ePayment) for
            engangsgaver. Begge bestilles på{" "}
            <ExternalLink href={VIPPS_PORTAL_URL}>
              portal.vippsmobilepay.com
            </ExternalLink>
            , og Vipps må godkjenne.
          </li>
          <li>
            API-nøklene og MSN må høre til <b>samme salgssted</b> som har
            produktene. Har moskeen flere salgssteder, må dere bruke riktig.
          </li>
          <li>
            Dere trenger ikke sette opp redirect-lenke eller webhook. iBønn
            sender med returlenken, sjekker status og trekker beløpene selv.
          </li>
        </Requirements>
        {vippsStatus?.connected && (
          <div className={styles.connected}>
            <dl className={styles.dl}>
              {vippsStatus.msn && (
                <>
                  <dt>MSN</dt>
                  <dd>{vippsStatus.msn}</dd>
                </>
              )}
              {vippsStatus.clientId && (
                <>
                  <dt>Client ID</dt>
                  <dd className={styles.mono}>{vippsStatus.clientId}</dd>
                </>
              )}
              <dt>Modus</dt>
              <dd>
                {vippsStatus.testMode ? (
                  <Badge tone="gold">Testmodus</Badge>
                ) : (
                  "Ekte betalinger"
                )}
              </dd>
            </dl>
            <div className={styles.actions}>
              {!vippsEditing && (
                <Button
                  variant="secondary"
                  onClick={() => setVippsEditing(true)}
                  disabled={!serverReady}
                >
                  Bytt nøkler
                </Button>
              )}
              <Button variant="danger" onClick={() => disconnect("vipps")}>
                Koble fra
              </Button>
            </div>
          </div>
        )}

        {showForm && (
          <div className={styles.form}>
            <ol className={styles.steps}>
              <li>
                Moskeen må ha en avtale med Vipps MobilePay med produktet «Faste
                betalinger» (Recurring), og gjerne «Betaling på nett» (ePayment)
                for engangsgaver.
              </li>
              <li>
                Logg inn på{" "}
                <ExternalLink href={VIPPS_PORTAL_URL}>
                  portal.vippsmobilepay.com
                </ExternalLink>{" "}
                og gå til <b>Utvikler → API-nøkler</b>.
              </li>
              <li>
                Velg salgsstedet og kopier <b>Client ID</b>,{" "}
                <b>Client secret</b>, <b>Ocp-Apim-Subscription-Key</b>{" "}
                (primærnøkkel) og <b>Merchant Serial Number (MSN)</b>.
              </li>
              <li>
                Lim dem inn under og trykk «Koble til Vipps». iBønn sjekker at
                nøklene virker før de lagres.
              </li>
            </ol>

            <div className={styles.grid}>
              <TextField
                label="Client ID"
                autoComplete="off"
                spellCheck={false}
                value={vipps.clientId}
                onChange={(e) => set("clientId", e.target.value)}
                disabled={!serverReady}
              />
              <TextField
                label="Merchant Serial Number (MSN)"
                inputMode="numeric"
                autoComplete="off"
                placeholder="123456"
                value={vipps.msn}
                onChange={(e) => set("msn", e.target.value)}
                disabled={!serverReady}
              />
              <TextField
                label="Client secret"
                type="password"
                autoComplete="off"
                value={vipps.clientSecret}
                onChange={(e) => set("clientSecret", e.target.value)}
                disabled={!serverReady}
              />
              <TextField
                label="Ocp-Apim-Subscription-Key"
                type="password"
                autoComplete="off"
                help="Primærnøkkelen."
                value={vipps.subscriptionKey}
                onChange={(e) => set("subscriptionKey", e.target.value)}
                disabled={!serverReady}
              />
            </div>
            <Toggle
              label={
                <span className={styles.toggleLabel}>
                  Testmodus (apitest.vipps.no)
                </span>
              }
              checked={vipps.testMode}
              onChange={(e) => set("testMode", e.target.checked)}
              disabled={!serverReady}
            />
            <span className={styles.help}>
              Hemmelige nøkler lagres kryptert og vises aldri igjen.
            </span>
            {vippsError && <Alert tone="error">{vippsError}</Alert>}
            <div className={styles.actions}>
              {vippsEditing && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setVippsEditing(false);
                    setVippsError(null);
                  }}
                >
                  Avbryt
                </Button>
              )}
              <Button
                onClick={saveVipps}
                loading={vippsSaving}
                disabled={!serverReady || !vippsValid}
                icon={<Icon name="link" size={16} strokeWidth={2} />}
              >
                Koble til Vipps
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <>
      <PageHeader
        title="Betaling"
        subtitle="Koble moskeens egen Vipps- og Stripe-konto til iBønn, så kan folk gi til kampanjene rett i appen."
      />

      <div className={styles.page}>
        <Alert tone="info">
          Pengene går rett til moskeens egen konto. iBønn har ingen
          betalingskonto og tar ikke imot pengene. Vipps og Stripe tar sine
          vanlige gebyrer etter moskeens avtale.
        </Alert>

        {!serverReady && (
          <Alert tone="warn">
            Betaling er ikke satt opp på serveren ennå, så du kan ikke koble til
            nå. Ta kontakt med iBønn.
          </Alert>
        )}

        {loadError && (
          <Alert tone="error">
            Kunne ikke hente betalingsoppsettet.{" "}
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                setIsLoading(true);
                load();
              }}
            >
              Prøv igjen
            </button>
          </Alert>
        )}

        <Tabs
          label="Betalingsløsning"
          value={tab}
          onChange={setTab}
          tabs={[
            {
              value: "vipps",
              label: "Vipps",
              extra: vippsStatus?.connected ? (
                <span className={styles.dot} aria-label="tilkoblet" />
              ) : undefined,
            },
            {
              value: "stripe",
              label: "Stripe",
              extra: stripe?.connected ? (
                <span className={styles.dot} aria-label="tilkoblet" />
              ) : undefined,
            },
          ]}
        />

        {isLoading ? (
          <Skeleton height={420} radius={20} />
        ) : loadError ? null : tab === "vipps" ? (
          renderVipps()
        ) : (
          renderStripe()
        )}
      </div>
    </>
  );
};

export default PaymentSettingsPage;
